(function () {
  const BUILDERS = "[data-exostrut-builder]";
  const STOCK_LENGTHS = [12, 24, 36, 48];

  function upper(value) {
    return String(value || "").trim().toUpperCase();
  }

  function normalized(value) {
    return upper(value).replace(/[^A-Z0-9]+/g, " ").trim();
  }

  function clampInteger(value, minimum, maximum, fallback) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(maximum, Math.max(minimum, parsed));
  }

  function createId(prefix) {
    const random = window.crypto && window.crypto.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint8Array(3)), (part) => part.toString(16).padStart(2, "0")).join("")
      : Math.random().toString(16).slice(2, 8);
    return `${prefix}-${Date.now().toString(36).toUpperCase()}-${random.toUpperCase()}`;
  }

  function money(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format((Number(cents) || 0) / 100);
  }

  class ExostrutBuilder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector("[data-exostrut-form]");
      this.mode = root.dataset.mode || "stock";
      this.purchaseLocked = root.dataset.purchaseLocked === "true";
      this.variants = this.readVariants();
      this.profileOptions = this.readJson("[data-exostrut-profile-options]", []);
      this.thumbnails = this.readJson("[data-exostrut-thumbnails]", {});
      this.models = this.readModels();
      this.activeModelKey = "";
      this.configurationId = createId(this.mode === "welded" ? "EXS-WLD" : "EXS-STD");
      this.buildLineId = createId("EXS-LINE");
      this.bypassSubmit = false;
      const initialVariant = this.variants.find((variant) => String(variant.id) === String(root.dataset.initialVariantId)) || this.variants[0] || null;
      const initialProfile = initialVariant?.options?.[0] || this.profileOptions[0] || "";
      const initialPresentation = this.profilePresentation(initialProfile);
      this.state = {
        profile: initialProfile,
        styleKey: initialPresentation.styleKey,
        materialCode: initialPresentation.materialCode,
        lengthRoute: "stock",
        lengthInches: 12,
        exactInches: 60,
        quantity: 1,
        issuedX: null,
        issueError: "",
        issuing: false,
        ends: {
          a: { cut: "Square / 90 deg", customAngle: "", termination: "Open", interface: "Not applicable" },
          b: { cut: "Square / 90 deg", customAngle: "", termination: "Open", interface: "Not applicable" },
        },
      };
      this.renderProfileAxes();
      this.bind();
      this.bindModelViewer();
      this.update();
    }

    readVariants() {
      const script = this.root.querySelector("[data-exostrut-variants]");
      try { return JSON.parse(script?.textContent || "[]"); } catch (error) { return []; }
    }

    readJson(selector, fallback) {
      const script = this.root.querySelector(selector);
      try { return JSON.parse(script?.textContent || JSON.stringify(fallback)); } catch (error) { return fallback; }
    }

    readModels() {
      const script = this.root.querySelector("[data-exostrut-models]");
      let records = [];
      try { records = JSON.parse(script?.textContent || "[]"); } catch (error) { records = []; }
      return records.reduce((catalog, record) => {
        const bundled = String(record.alt || "").match(/EXOSTRUT\s*\/\s*(P1000|P3300)\s*\/\s*(SOLID|SLOTTED)\s*\/\s*(12|24|36|48)\s*IN/i);
        if (bundled) catalog.set(`${upper(bundled[1])}|${upper(bundled[2])}|${Number(bundled[3])}`, record);
        return catalog;
      }, new Map());
    }

    bindModelViewer() {
      const viewer = this.root.querySelector("[data-exostrut-model-viewer]");
      const status = this.root.querySelector("[data-exostrut-model-status]");
      if (!viewer) return;
      viewer.addEventListener("load", () => {
        this.applyModelMaterial();
        if (status) status.textContent = `CONTROLLED ${this.inferMaterialCode()} MODEL READY / DRAG TO ROTATE / AR AVAILABLE`;
        viewer.dispatchEvent(new CustomEvent("exo:model-viewer-ready"));
      });
      viewer.addEventListener("error", () => {
        if (status) status.textContent = "3D MODEL UNAVAILABLE / CONTROLLED PROFILE PROOF SHOWN";
        this.showCssProof();
      });
    }

    selectedChoice(name) {
      const selected = this.root.querySelector(`[data-exostrut-choice="${name}"].is-selected`);
      return selected ? selected.dataset.value || selected.textContent.trim() : "";
    }

    clearIssuedX() {
      this.state.issuedX = null;
      this.state.issueError = "";
    }

    bind() {
      this.root.querySelectorAll("[data-exostrut-axis-choice]").forEach((button) => {
        button.addEventListener("click", () => {
          if (button.disabled) return;
          const axis = button.dataset.exostrutAxisChoice;
          this.state[axis === "style" ? "styleKey" : "materialCode"] = button.dataset.value || "";
          const match = this.availableProfileRecords().find((record) => record.styleKey === this.state.styleKey && record.materialCode === this.state.materialCode);
          if (match) this.state.profile = match.value;
          this.clearIssuedX();
          this.update();
        });
      });

      this.root.querySelectorAll("[data-exostrut-choice]").forEach((button) => {
        button.addEventListener("click", () => {
          const name = button.dataset.exostrutChoice;
          this.root.querySelectorAll(`[data-exostrut-choice="${name}"]`).forEach((peer) => {
            const active = peer === button;
            peer.classList.toggle("is-selected", active);
            peer.setAttribute("aria-pressed", active ? "true" : "false");
          });
          this.state[name] = button.dataset.value || "";
          const picker = button.closest("[data-exostrut-profile-picker]");
          if (picker) picker.open = false;
          this.clearIssuedX();
          this.update();
        });
      });

      this.root.querySelectorAll("[data-exostrut-length-preset]").forEach((button) => {
        button.addEventListener("click", () => {
          const selected = button.dataset.exostrutLengthPreset;
          this.state.lengthRoute = selected === "x" ? "x" : "stock";
          if (selected !== "x") this.state.lengthInches = Number(selected);
          this.clearIssuedX();
          this.update();
        });
      });

      const exactInput = this.root.querySelector("[data-exostrut-exact-inches]");
      const quantityInput = this.root.querySelector("[data-exostrut-quantity]");
      exactInput.addEventListener("input", () => {
        this.state.exactInches = clampInteger(exactInput.value, 1, 240, 60);
        this.clearIssuedX();
        this.update();
      });
      quantityInput.addEventListener("input", () => {
        this.state.quantity = clampInteger(quantityInput.value, 1, 999, 1);
        this.update();
      });
      exactInput.addEventListener("blur", () => { exactInput.value = this.state.exactInches; });
      quantityInput.addEventListener("blur", () => { quantityInput.value = this.state.quantity; });

      this.root.querySelectorAll("[data-exostrut-end]").forEach((container) => {
        const key = container.dataset.exostrutEnd;
        const cut = container.querySelector("[data-exostrut-end-cut]");
        const custom = container.querySelector("[data-exostrut-custom-angle]");
        const customInput = custom.querySelector("input");
        const termination = container.querySelector("[data-exostrut-end-termination]");
        const interfaceRow = container.querySelector("[data-exostrut-interface]");
        const interfaceInput = container.querySelector("[data-exostrut-end-interface]");
        cut.addEventListener("change", () => {
          custom.hidden = cut.value !== "Custom angle / review";
          this.state.ends[key].cut = cut.value;
          this.state.ends[key].customAngle = custom.hidden ? "" : customInput.value;
          this.clearIssuedX();
          this.update();
        });
        customInput.addEventListener("input", () => {
          this.state.ends[key].customAngle = customInput.value;
          this.clearIssuedX();
          this.update();
        });
        termination.addEventListener("change", () => {
          interfaceRow.hidden = termination.value !== "Welded mounting cap";
          this.state.ends[key].termination = termination.value;
          this.state.ends[key].interface = interfaceRow.hidden ? "Not applicable" : interfaceInput.value;
          this.clearIssuedX();
          this.update();
        });
        interfaceInput.addEventListener("change", () => {
          this.state.ends[key].interface = interfaceInput.value;
          this.clearIssuedX();
          this.update();
        });
      });

      this.form.addEventListener("submit", async (event) => {
        if (this.bypassSubmit) return;
        const release = this.releaseState();
        if (this.state.lengthRoute === "x" && release.canIssueX && !this.state.issuedX) {
          event.preventDefault();
          await this.issueExactLength(release.baseVariant);
          if (this.state.issuedX) {
            this.bypassSubmit = true;
            this.form.requestSubmit();
          }
          return;
        }
        if (!release.directPurchase) {
          event.preventDefault();
          const inquiry = this.root.querySelector("[data-exostrut-inquiry]");
          if (inquiry && !inquiry.hidden && inquiry.href) window.location.assign(inquiry.href);
        }
      });
    }

    totalInches() {
      return this.state.lengthRoute === "x" ? this.state.exactInches : this.state.lengthInches;
    }

    normalizedLength() {
      const inches = this.totalInches();
      const feet = Math.floor(inches / 12);
      const remainder = inches % 12;
      return `${feet} ft ${remainder} in (${inches} in)`;
    }

    inferMaterial() {
      const material = this.inferMaterialCode();
      if (material === "SS304") return "304 stainless steel";
      if (material === "ALUMINUM") return "Aluminum";
      if (material === "G90") return "Pre-galvanized steel";
      return "Raw steel";
    }

    inferMaterialCode() {
      const profile = upper(this.state.profile);
      if (profile.includes("SS304") || profile.includes("STAINLESS")) return "SS304";
      if (profile.includes("ALUM")) return "ALUMINUM";
      if (profile.includes("GALV") || profile.includes("G90")) return "G90";
      return "STEEL";
    }

    inferProfileCode() {
      const profile = upper(this.state.profile);
      return profile.includes("P3300") || profile.includes("SLIM") ? "P3300" : "P1000";
    }

    inferPatternCode() {
      return upper(this.state.profile).includes("SLOT") ? "SLOTTED" : "SOLID";
    }

    profilePresentation(value = this.state.profile) {
      const text = upper(value);
      const profile = text.includes("P3300") || text.includes("SLIM") ? "P3300" : "P1000";
      const pattern = text.includes("SLOT") ? "SLOTTED" : "SOLID";
      const size = profile === "P3300" ? "13/16 IN" : "1-5/8 IN";
      const gaugeMatch = text.match(/\b\d{1,2}\s*GA\b/);
      const gauge = gaugeMatch ? gaugeMatch[0].replace(/\s+/g, " ") : "GAUGE BY SKU";
      let materialCode = "STEEL";
      if (text.includes("SS304") || text.includes("STAINLESS")) materialCode = "SS304";
      else if (text.includes("ALUM")) materialCode = "ALUMINUM";
      else if (text.includes("GALV") || text.includes("G90")) materialCode = "G90";
      const styleKey = `${profile}|${pattern}`;
      const titles = {
        "P1000|SOLID": "Standard solid channel",
        "P1000|SLOTTED": "Standard slotted channel",
        "P3300|SOLID": "Low-profile solid channel",
        "P3300|SLOTTED": "Low-profile slotted channel",
      };
      const materialLabels = {
        G90: "Pre-galvanized steel",
        ALUMINUM: "Aluminum",
        SS304: "304 stainless steel",
        STEEL: "Raw steel",
      };
      return {
        profile,
        pattern,
        size,
        gauge,
        materialCode,
        material: materialLabels[materialCode],
        styleKey,
        title: titles[styleKey],
        meta: `${profile} / ${size} / ${gauge}`,
      };
    }

    availableProfileRecords() {
      return this.profileOptions
        .map((value) => ({ value, ...this.profilePresentation(value) }))
        .filter((record) => this.mode !== "stock" || record.materialCode !== "STEEL");
    }

    thumbnailUrl(presentation, materialCode = presentation.materialCode) {
      return this.thumbnails[`${presentation.profile}|${presentation.pattern}|${materialCode}`] || "";
    }

    renderProfileAxes() {
      const records = this.availableProfileRecords();
      const styleContainer = this.root.querySelector('[data-exostrut-axis-options="style"]');
      const materialContainer = this.root.querySelector('[data-exostrut-axis-options="material"]');
      const styles = Array.from(new Map(records.map((record) => [record.styleKey, record])).values());
      const materials = Array.from(new Map(records.map((record) => [record.materialCode, record])).values());
      if (styleContainer) {
        styleContainer.innerHTML = styles.map((record) => `
          <button type="button" class="exostrut-axis-choice" data-exostrut-axis-choice="style" data-value="${record.styleKey}" aria-pressed="false">
            <img class="exostrut-profile-thumbnail" src="${this.thumbnailUrl(record, this.state.materialCode)}" alt="${record.title}" width="96" height="96">
            <span><strong>${record.title}</strong><small>${record.meta}</small></span>
          </button>`).join("");
      }
      if (materialContainer) {
        const ordered = ["G90", "ALUMINUM", "SS304"];
        materialContainer.innerHTML = ordered.map((code) => {
          const record = materials.find((candidate) => candidate.materialCode === code);
          const disabled = !record;
          const label = { G90: "Pre-galvanized steel", ALUMINUM: "Aluminum", SS304: "304 stainless steel" }[code];
          return `
            <button type="button" class="exostrut-axis-choice exostrut-axis-choice--material" data-exostrut-axis-choice="material" data-value="${code}" aria-pressed="false" ${disabled ? "disabled" : ""}>
              <img class="exostrut-profile-thumbnail" src="${this.thumbnailUrl(this.profilePresentation(this.state.profile), code)}" alt="" width="96" height="96">
              <span><strong>${label}</strong><small>${disabled ? "Catalog variant release pending" : "Released catalog material"}</small></span>
            </button>`;
        }).join("");
      }
    }

    updateProfilePresentation() {
      const selected = this.profilePresentation();
      this.state.styleKey = selected.styleKey;
      this.state.materialCode = selected.materialCode;
      this.root.querySelectorAll("[data-exostrut-axis-choice]").forEach((button) => {
        const stateKey = button.dataset.exostrutAxisChoice === "style" ? "styleKey" : "materialCode";
        const active = button.dataset.value === this.state[stateKey];
        button.classList.toggle("is-selected", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
        if (button.dataset.exostrutAxisChoice === "style") {
          const image = button.querySelector("img");
          const record = this.availableProfileRecords().find((candidate) => candidate.styleKey === button.dataset.value) || selected;
          if (image) image.src = this.thumbnailUrl(record, selected.materialCode);
        }
      });
      const title = this.root.querySelector("[data-exostrut-selected-title]");
      const meta = this.root.querySelector("[data-exostrut-selected-meta]");
      if (title) title.textContent = selected.title;
      if (meta) meta.textContent = selected.meta;
      const thumbnail = this.root.querySelector("[data-exostrut-spec-thumbnail]");
      if (thumbnail) {
        thumbnail.src = this.thumbnailUrl(selected);
        thumbnail.alt = `${selected.title}, ${selected.material}`;
      }
      return selected;
    }

    representativeModelLength() {
      const requested = this.totalInches();
      return STOCK_LENGTHS.reduce((closest, length) => Math.abs(length - requested) < Math.abs(closest - requested) ? length : closest, STOCK_LENGTHS[0]);
    }

    sourceByMime(record, token) {
      return (record?.sources || []).find((source) => {
        const value = upper([source.mime_type, source.mimeType, source.format].filter(Boolean).join(" "));
        return value.includes(token);
      });
    }

    applyModelMaterial() {
      const viewer = this.root.querySelector("[data-exostrut-model-viewer]");
      if (!viewer) return;
      const material = this.inferMaterialCode();
      viewer.setAttribute("variant-name", material);
      if ("variantName" in viewer) viewer.variantName = material;
      this.activeModelMaterial = material;
    }

    showCssProof() {
      const stage = this.root.querySelector("[data-exostrut-model-stage]");
      const cssProof = this.root.querySelector("[data-exostrut-css-proof]");
      if (stage) stage.hidden = true;
      if (cssProof) cssProof.hidden = false;
    }

    updateModelProof() {
      if (!this.models.size) { this.showCssProof(); return; }
      const length = this.representativeModelLength();
      const key = `${this.inferProfileCode()}|${this.inferPatternCode()}|${length}`;
      const record = this.models.get(key);
      const glb = this.sourceByMime(record, "GLTF-BINARY") || this.sourceByMime(record, "GLB");
      if (!record || !glb?.url) { this.showCssProof(); return; }

      const stage = this.root.querySelector("[data-exostrut-model-stage]");
      const cssProof = this.root.querySelector("[data-exostrut-css-proof]");
      const viewer = this.root.querySelector("[data-exostrut-model-viewer]");
      const status = this.root.querySelector("[data-exostrut-model-status]");
      if (!stage || !viewer) return;
      stage.hidden = false;
      if (cssProof) cssProof.hidden = true;

      if (key !== this.activeModelKey) {
        this.activeModelKey = key;
        if (record.preview) viewer.setAttribute("poster", record.preview);
        viewer.setAttribute("src", glb.url);
        // Keep ios-src unset so model-viewer exports the currently selected
        // material variant to Quick Look instead of using Shopify's default-
        // material USDZ conversion.
        viewer.removeAttribute("ios-src");
        viewer.setAttribute("alt", record.alt || "Interactive EXOSTRUT configuration");
        if (status) status.textContent = "LOADING CONTROLLED EXOSTRUT MODEL";
      }
      if (this.activeModelMaterial !== this.inferMaterialCode()) this.applyModelMaterial();
      if (status && this.state.lengthRoute === "x") {
        status.textContent = `${length} IN REPRESENTATIVE MODEL / ${this.totalInches()} IN EXACT LENGTH CONTROLS ORDER`;
      }
    }

    inferFinish() {
      const material = this.inferMaterialCode();
      if (material === "G90") return "Pre-galvanized / catalog finish";
      if (material === "SS304") return "Mill / catalog stainless finish";
      if (material === "ALUMINUM") return "Mill / catalog aluminum finish";
      if (material === "STEEL") return "Raw / fabrication finish pending";
      return "Catalog finish";
    }

    variantText(variant) {
      return normalized([variant.title, variant.sku, ...(variant.options || [])].join(" "));
    }

    variantMatchesLength(variant, lengthInches) {
      const accepted = new Set([`${lengthInches}`, `${lengthInches} IN`, `${lengthInches} INCH`, `${lengthInches} INCHES`]);
      if (lengthInches % 12 === 0) accepted.add(`${lengthInches / 12} FT`);
      return (variant.options || []).some((option) => accepted.has(normalized(option)));
    }

    endToken(end) {
      if (end.termination === "Welded mounting cap") return "MOUNT";
      if (end.termination === "Seamless welded flush cap") return "FLUSH";
      return "OPEN";
    }

    findNativeVariant(lengthInches) {
      const profile = normalized(this.state.profile);
      const candidates = this.variants.filter((variant) => this.variantMatchesLength(variant, lengthInches));
      const profileCandidates = candidates.filter((variant) => (variant.options || []).some((option) => normalized(option) === profile));
      if (this.mode === "stock") return profileCandidates[0] || null;
      const endA = this.endToken(this.state.ends.a);
      const endB = this.endToken(this.state.ends.b);
      return profileCandidates.find((variant) => {
        const text = this.variantText(variant);
        const endsMatch = text.includes(`A ${endA}`) && text.includes(`B ${endB}`);
        return endsMatch;
      }) || null;
    }

    needsDrawingReview() {
      if (this.mode !== "welded") return false;
      return ["a", "b"].some((key) => {
        const end = this.state.ends[key];
        return end.cut !== "Square / 90 deg"
          || end.interface === "Custom hole pattern / review";
      });
    }

    releaseState() {
      const length = this.totalInches();
      const validLength = Number.isInteger(length) && length >= 1 && length <= 240;
      const stockConflict = this.state.lengthRoute === "x" && STOCK_LENGTHS.includes(length);
      const drawingReview = this.needsDrawingReview();
      const variant = this.state.lengthRoute === "stock" && validLength ? this.findNativeVariant(length) : null;
      const baseVariant = this.state.lengthRoute === "x" ? this.findNativeVariant(12) : null;
      const canIssueX = !this.purchaseLocked && validLength && !stockConflict && !drawingReview && !!baseVariant;
      const directPurchase = this.state.lengthRoute === "x"
        ? canIssueX && !!this.state.issuedX
        : !this.purchaseLocked && !drawingReview && !!variant && variant.available && Number(variant.price) > 0;
      return { validLength, stockConflict, variant, baseVariant, drawingReview, canIssueX, directPurchase };
    }

    async issueExactLength(baseVariant) {
      this.state.issuing = true;
      this.state.issueError = "";
      this.update();
      try {
        const response = await fetch(this.root.dataset.xEndpoint, {
          method: "POST",
          headers: { "content-type": "application/json", "accept": "application/json" },
          body: JSON.stringify({
            sourceProductId: this.root.dataset.productId,
            sourceProductHandle: this.root.dataset.productHandle,
            baseVariantId: baseVariant.id,
            lengthInches: this.totalInches(),
            configurationId: this.configurationId,
            profile: this.state.profile,
            material: this.inferMaterial(),
            finish: this.inferFinish(),
            endA: this.endDisplay("a"),
            endB: this.endDisplay("b"),
          }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.exactLength) throw new Error(payload.error || "Exact-length pricing failed.");
        this.state.issuedX = payload.exactLength;
      } catch (error) {
        this.state.issueError = error.message || "Exact-length pricing failed.";
      } finally {
        this.state.issuing = false;
        this.update();
      }
    }

    cutToken(cut) {
      if (cut.startsWith("30")) return "30";
      if (cut.startsWith("45")) return "45";
      if (cut.startsWith("Custom")) return "custom";
      return "square";
    }

    terminationToken(termination) {
      if (termination === "Welded mounting cap") return "mounting";
      if (termination === "Seamless welded flush cap") return "flush";
      return "open";
    }

    endDisplay(key) {
      const end = this.state.ends[key];
      const cut = end.cut === "Custom angle / review" && end.customAngle ? `${end.customAngle} deg custom / review` : end.cut;
      return `${cut} / ${end.termination}${end.interface !== "Not applicable" ? ` / ${end.interface}` : ""}`;
    }

    setProperty(key, value) {
      const input = this.root.querySelector(`[data-exostrut-property="${key}"]`);
      if (input) input.value = value;
    }

    updateProperties(release) {
      const material = this.inferMaterial();
      const finish = this.inferFinish();
      this.setProperty("configuration-id", this.configurationId);
      this.setProperty("build-line-id", this.buildLineId);
      this.setProperty("profile", this.state.profile);
      this.setProperty("material", material);
      this.setProperty("finish", finish);
      this.setProperty("length", this.normalizedLength());
      this.setProperty("length-route", this.state.lengthRoute === "x" ? "X / EXACT INCHES" : "STOCK");
      this.setProperty("x-base-variant", release.baseVariant?.id || "");
      this.setProperty("x-price-key", this.state.issuedX?.sku || "");
      this.setProperty("piece-quantity", String(this.state.quantity));
      this.setProperty("end-a-cut", this.state.ends.a.cut === "Custom angle / review" ? `${this.state.ends.a.customAngle || "Unspecified"} deg custom / review` : this.state.ends.a.cut);
      this.setProperty("end-a-termination", this.state.ends.a.termination);
      this.setProperty("end-a-interface", this.state.ends.a.interface);
      this.setProperty("end-b-cut", this.state.ends.b.cut === "Custom angle / review" ? `${this.state.ends.b.customAngle || "Unspecified"} deg custom / review` : this.state.ends.b.cut);
      this.setProperty("end-b-termination", this.state.ends.b.termination);
      this.setProperty("end-b-interface", this.state.ends.b.interface);
      this.setProperty("proof-status", release.drawingReview ? "Customer preview / custom fabrication drawing review required" : "Customer configuration preview / final fabrication drawing controls production");
      this.setProperty("review-status", this.state.issuedX ? "App-issued exact-length configuration" : release.directPurchase ? "Released catalog configuration" : "Pricing and release review required");
    }

    updateProof() {
      const material = this.inferMaterial();
      const finish = this.inferFinish();
      const presentation = this.updateProfilePresentation();
      const proofLength = this.root.querySelector("[data-exostrut-proof-length]");
      if (proofLength) proofLength.textContent = `${this.totalInches()} IN / ${this.state.lengthRoute === "x" ? "X EXACT" : "STOCK"}`;
      const configId = this.root.querySelector("[data-exostrut-config-id]");
      if (configId) configId.textContent = this.configurationId;
      const profile = this.root.querySelector('[data-exostrut-summary="profile"]');
      const materialFinish = this.root.querySelector('[data-exostrut-summary="material-finish"]');
      const quantity = this.root.querySelector('[data-exostrut-summary="quantity"]');
      if (profile) profile.textContent = presentation.title;
      if (materialFinish) materialFinish.textContent = presentation.material;
      if (quantity) quantity.textContent = String(this.state.quantity);
      const specLength = this.root.querySelector('[data-exostrut-spec="length"]');
      const specDimensions = this.root.querySelector('[data-exostrut-spec="dimensions"]');
      if (specLength) specLength.textContent = `${this.totalInches()} IN`;
      if (specDimensions) specDimensions.textContent = `${presentation.size} / ${presentation.gauge}`;
      const recordValues = {
        profile: this.state.profile || "Pending",
        material: `${material} / ${finish}`,
        length: this.normalizedLength(),
        quantity: String(this.state.quantity),
        id: this.configurationId,
      };
      Object.entries(recordValues).forEach(([key, value]) => {
        const target = this.root.querySelector(`[data-exostrut-record="${key}"]`);
        if (target) target.textContent = value;
      });
      this.updateModelProof();
      ["a", "b"].forEach((key) => {
        const visual = this.root.querySelector(`[data-exostrut-proof-end="${key}"]`);
        if (visual) {
          visual.dataset.cut = this.cutToken(this.state.ends[key].cut);
          visual.dataset.termination = this.terminationToken(this.state.ends[key].termination);
        }
        const summary = this.root.querySelector(`[data-exostrut-end="${key}"] [data-exostrut-end-summary]`);
        if (summary) summary.textContent = `END ${key.toUpperCase()} / ${this.endDisplay(key).toUpperCase()}`;
      });
    }

    updateLengthControls() {
      this.root.querySelectorAll("[data-exostrut-length-preset]").forEach((button) => {
        const key = button.dataset.exostrutLengthPreset;
        const active = key === "x" ? this.state.lengthRoute === "x" : this.state.lengthRoute === "stock" && Number(key) === this.state.lengthInches;
        button.classList.toggle("is-selected", active);
      });
      const exactField = this.root.querySelector("[data-exostrut-exact-field]");
      if (exactField) exactField.hidden = this.state.lengthRoute !== "x";
    }

    updateAction(release) {
      const variant = this.state.lengthRoute === "x" ? this.state.issuedX : release.variant;
      const variantInput = this.root.querySelector("[data-exostrut-variant]");
      if (variant && variantInput) variantInput.value = variant.variantId || variant.id;
      const submit = this.root.querySelector("[data-exostrut-submit]");
      const inquiry = this.root.querySelector("[data-exostrut-inquiry]");
      const actionable = release.directPurchase || release.canIssueX;
      if (submit) {
        submit.hidden = !actionable;
        submit.disabled = this.state.issuing;
        const label = this.state.issuing ? "Pricing exact length..." : this.state.lengthRoute === "x" && !this.state.issuedX ? "Price + add exact length" : this.mode === "welded" ? "Add fabricated strut to build" : "Add straight strut to build";
        const labelElement = submit.querySelector("[data-exostrut-submit-label]");
        if (labelElement) labelElement.textContent = label;
      }
      if (inquiry) inquiry.hidden = actionable;
      const material = this.inferMaterial();
      const finish = this.inferFinish();
      const title = this.root.querySelector("[data-exostrut-build-title]");
      const note = this.root.querySelector("[data-exostrut-build-note]");
      const record = this.root.querySelector("[data-exostrut-build-record]");
      const total = this.root.querySelector("[data-exostrut-total]");
      const presentation = this.profilePresentation();
      if (title) title.textContent = `${presentation.title} / ${this.totalInches()} IN / ${this.state.quantity} ${this.state.quantity === 1 ? "PC" : "PCS"}`;
      if (note) note.textContent = this.mode === "welded"
        ? `End A: ${this.endDisplay("a")} / End B: ${this.endDisplay("b")}`
        : `${presentation.meta}. Straight square-cut stock.`;
      if (record) record.textContent = this.mode === "welded"
        ? `${this.state.profile} / ${material} / ${finish} / ${this.normalizedLength()} / ${this.state.quantity} PCS / End A: ${this.endDisplay("a")} / End B: ${this.endDisplay("b")}`
        : `${this.state.profile} / ${material} / ${finish} / ${this.normalizedLength()} / ${this.state.quantity} PCS`;
      if (total) {
        if (this.state.issuedX) total.textContent = `${money(this.state.issuedX.priceCents * this.state.quantity)} / ${this.state.quantity} PCS`;
        else if (release.directPurchase) total.textContent = `${money(Number(release.variant.price) * this.state.quantity)} / ${this.state.quantity} PCS`;
        else if (release.canIssueX) total.textContent = "EXACT PRICE AT CART";
        else total.textContent = release.validLength ? "PRICING REVIEW" : "CHECK LENGTH";
      }
      const actionNote = this.root.querySelector("[data-exostrut-action-note]");
      if (actionNote) {
        if (this.state.issueError) actionNote.textContent = this.state.issueError;
        else if (!release.validLength) actionNote.textContent = "Exact finished length must be a whole inch from 1 through 240.";
        else if (release.stockConflict) actionNote.textContent = `Use the native ${this.totalInches()} IN stock button for this length.`;
        else if (release.drawingReview) actionNote.textContent = "This end geometry requires a reviewed fabrication drawing before release.";
        else if (release.canIssueX && !this.state.issuedX) actionNote.textContent = "EXO-OS will calculate and save the exact-inch price before adding one finished-piece cart line.";
        else if (!release.directPurchase) actionNote.textContent = "A matching released 12 IN base configuration is required before X can be priced.";
        else actionNote.textContent = "The complete specification will stay with this cart line.";
      }
      if (inquiry) {
        const url = new URL(this.root.dataset.inquiryBase || "/pages/contact?route=exo-general", window.location.origin);
        url.searchParams.set("product", this.root.dataset.productTitle || "EXOSTRUT");
        url.searchParams.set("handle", this.root.dataset.productHandle || "");
        url.searchParams.set("configuration", this.configurationId);
        url.searchParams.set("profile", this.state.profile);
        url.searchParams.set("length", this.normalizedLength());
        url.searchParams.set("quantity", String(this.state.quantity));
        if (this.mode === "welded") {
          url.searchParams.set("material", material);
          url.searchParams.set("finish", finish);
          url.searchParams.set("end_a", this.endDisplay("a"));
          url.searchParams.set("end_b", this.endDisplay("b"));
        }
        inquiry.href = `${url.pathname}${url.search}`;
      }
    }

    update() {
      this.state.exactInches = clampInteger(this.state.exactInches, 1, 240, 60);
      this.state.quantity = clampInteger(this.state.quantity, 1, 999, 1);
      const release = this.releaseState();
      this.updateProperties(release);
      this.updateProof();
      this.updateLengthControls();
      this.updateAction(release);
      this.dispatchVariantContext(release);
    }

    dispatchVariantContext(release) {
      const current = this.state.lengthRoute === "x" ? this.state.issuedX || release.baseVariant : release.variant;
      if (!current) return;
      const presentation = this.profilePresentation();
      document.dispatchEvent(new CustomEvent("exo:variant-change", {
        detail: {
          variant: {
            id: current.variantId || current.id,
            sku: current.sku || "",
            title: current.title || `${presentation.title} / ${this.totalInches()} IN`,
            options: current.options || [this.state.profile, `${this.totalInches()} IN`],
          },
          selection: {
            family: "EXOSTRUT channel",
            channelStyle: presentation.title,
            manufacturerSeries: presentation.profile,
            profile: presentation.size,
            material: this.inferMaterial(),
            finish: this.inferFinish(),
            finishedLength: this.normalizedLength(),
            quantity: this.state.quantity,
            provenance: "Genuine Unistrut channel / exact manufacturer part number controls published technical data",
          },
        },
      }));
    }
  }

  function initialize(scope) {
    scope.querySelectorAll(BUILDERS).forEach((root) => {
      if (root.dataset.exostrutInitialized === "true") return;
      root.dataset.exostrutInitialized = "true";
      new ExostrutBuilder(root);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize(document));
  else initialize(document);
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
